import { catColors, backgroundColors } from "./data";
import {
  generatorSize,
  noseSize,
  earSize,
  colors,
} from "../../../../constants";
const { dark, pink } = colors;

/**
 * Get a random number between 2 values.
 */
export const generateClampedRandom = ({ min, max }) =>
  Math.random() * (max - min) + min;

/**
 * Convert a control point and coordinate point into a cubic
 * bezier string that svg's can understand.
 */
export const cubicPoint = (a, b) =>
  ` C ${a.x} ${a.y} ${a.x} ${a.y} ${b.x} ${b.y}`;

/**
 * Generate a random set of dimensions for the body of the cat.
 */
export const generateCatDimensions = () => {
  const halfGeneratorSize = generatorSize / 2;

  return {
    diameter: generateClampedRandom({
      min: halfGeneratorSize * 0.75,
      max: halfGeneratorSize * 1.1,
    }),
    height: generateClampedRandom({
      min: halfGeneratorSize,
      max: halfGeneratorSize * 1.5,
    }),
  };
};

/**
 * Generate a svg <path>'s 'd' attribute (as a string).
 * This takes a basic height as an argument, the amplitude
 * of the wave is generated at random.
 */
export const generateWavePath = (height) => {
  const pointCount = Math.floor(generateClampedRandom({ min: 3, max: 15 }));
  const amplitude = generateClampedRandom({
    min: generatorSize * 0.02,
    max: generatorSize * 0.05,
  });
  const containerWidth = 800;
  const containerHeight = 800;

  /**
   * Create a new array of coordinates, evenly spaced on the x
   * axis, and the y value is using a sine wave.
   */
  const points = [...new Array(pointCount + 1)].map((_, index) => ({
    x: (index / pointCount) * containerWidth,
    y: amplitude * Math.sin(index) + height,
  }));

  /**
   * This coordinate set gets updated throughout each point iteration so
   * that we can generate a decent cubic bezier control point.
   */
  let controlCoordinates = {
    x: (points[1].x - points[0].x) / 2,
    y: points[1].y - points[0].y + points[0].y + (points[1].y - points[0].y),
  };

  /**
   * The start to our svg's path 'd' attribute. We'll add to this by iterating
   * through each coordinate we've generatred.
   */
  let path = `M 0 ${height},`.concat(cubicPoint(controlCoordinates, points[1]));

  /**
   * Edit our control point on each point's coordinates, and generate
   * a cubic bezier point based on this and add it to the path string.
   */
  for (let i = 1; i < points.length - 1; i += 1) {
    controlCoordinates = {
      x: points[i].x - controlCoordinates.x + points[i].x,
      y: points[i].y - controlCoordinates.y + points[i].y,
    };

    path += cubicPoint(controlCoordinates, points[i + 1]);
  }

  /**
   * Finish off the path by joining it back up to the start
   * using basic lines (user won't be able to see these)
   */
  path += ` L ${containerWidth} ${containerHeight}`;
  path += ` L 0 ${containerHeight} Z`;

  return path;
};

/**
 * Grab a random colour scheme from our cat colours array.
 */
export const generateCatColors = () => {
  return catColors[Math.floor(Math.random() * catColors.length)];
};

/**
 * Grab a random colour scheme from our background colours array.
 */
export const generateBackgroundColors = () => {
  return backgroundColors[Math.floor(Math.random() * backgroundColors.length)];
};

export const generateBodyCurves = ({ start, midLeft, midRight, end }) => [
  `C ${start.x} ${midLeft.y} ${midLeft.x} ${midLeft.y} ${midLeft.x} ${midLeft.y}`,
  `C ${midLeft.x} ${midLeft.y} ${midRight.x} ${midRight.y} ${midRight.x} ${midRight.y}`,
  `C ${midRight.x} ${midRight.y} ${end.x} ${midRight.y} ${end.x} ${end.y}`,
];

/**
 * Generate a body path
 */
export const generateBodyPath = ({ diameter, height }) => {
  const halfGeneratorSize = generatorSize / 2;

  // Essentially, how long the top of their head is.
  const controlThreshold = generateClampedRandom({
    min: diameter * 0.05,
    max: diameter * 0.2,
  });

  const start = { x: halfGeneratorSize - diameter, y: generatorSize };
  const curves = generateBodyCurves({
    start,
    midLeft: {
      x: halfGeneratorSize - controlThreshold,
      y: generatorSize - height,
    },
    midRight: {
      x: halfGeneratorSize + controlThreshold,
      y: generatorSize - height,
    },
    end: { x: halfGeneratorSize + diameter, y: generatorSize },
  });

  return `M ${start.x} ${start.y},`
    .concat(curves.join(", "))
    .concat(` L ${start.x} ${generatorSize} Z`);
};

/**
 * Generate random eyes to be plugged into an svg <ellipse> element.
 * This has to take the diameter of the cat's body into account (as an argument).
 */
export const generateEyes = ({ diameter, height }) => {
  const isRound = Math.floor(Math.random());

  const containerConstraints = { min: diameter / 4, max: diameter / 2.5 };
  const eyeWidth = generateClampedRandom({ ...containerConstraints });
  const eyeHeight = isRound
    ? eyeWidth
    : generateClampedRandom({ min: containerConstraints.min, max: eyeWidth });

  const irisWidth = generateClampedRandom({
    min: eyeWidth / 2,
    max: eyeWidth - 10,
  });
  const irisHeight = isRound
    ? irisWidth
    : generateClampedRandom({ min: eyeHeight / 1.5, max: eyeHeight - 10 });

  const spacing = generateClampedRandom({ min: 0, max: diameter / 10 });
  const cy =
    generatorSize -
    height / 2 -
    generateClampedRandom({ min: 0, max: height / 10 });

  const eyeSchema = {
    // The complete size of the eye
    container: {
      rx: eyeWidth,
      ry: eyeHeight,
    },
    // The size of the inner iris
    inner: {
      rx: irisWidth,
      ry: irisHeight,
    },
    coordinates: {
      leftContainer: {
        cx: generatorSize / 2 - (spacing + eyeWidth),
        cy,
      },
      leftIris: {
        cx:
          generatorSize / 2 -
          (spacing + irisWidth) -
          (eyeWidth - irisWidth) / 1.5,
        cy,
      },
      rightContainer: {
        cx: generatorSize / 2 + (spacing + eyeWidth),
        cy,
      },
      rightIris: {
        cx:
          generatorSize / 2 +
          (spacing + irisWidth) +
          (eyeWidth - irisWidth) / 1.5,
        cy,
      },
    },
  };

  return eyeSchema;
};

/**
 * Generate a random nose size and placement.
 */
export const generateNose = ({ height }) => {
  const size = generateClampedRandom({ min: 0.5, max: 1.2 });
  const newNoseSize = noseSize * size;

  return {
    size,
    coordinates: {
      x: generatorSize / 2 - newNoseSize / 2,
      y:
        generatorSize -
        (height / 3 + generateClampedRandom({ min: 0, max: height / 10 })),
    },
  };
};

/**
 * Generate ears based on the cats dimensions
 */
export const generateEars = ({ diameter, height }) => {
  const scale = generateClampedRandom({ min: 0.8, max: 1 });
  const size = earSize * scale;
  const rotation = generateClampedRandom({ min: 0, max: 3 });
  const yPos = generatorSize - height - size / 4;

  return {
    scale,
    rotation,
    leftCoords: { x: generatorSize / 2 - diameter - size / 10, y: yPos },
    rightCoords: { x: generatorSize / 2 + diameter + size / 10, y: yPos },
  };
};

/**
 * Generate a randomised cat schema to plug in
 * to our display components.
 */
export const generateRandomSchema = () => {
  const catDimensions = generateCatDimensions();
  const backgroundWaves = [...Array(2)].map((_, index) => {
    const base = generatorSize * 0.15;
    const min = base + generatorSize * (index + 1.75) * (index * 0.1);
    const max = min * 1.5;
    return generateWavePath(generateClampedRandom({ min, max }));
  });

  const catColors = generateCatColors();

  return {
    dimensions: { ...catDimensions },
    eyes: {
      ...generateEyes({ ...catDimensions }),
      highlightOpacity: generateClampedRandom({ min: 0.1, max: 1 }),
    },
    ears: { ...generateEars({ ...catDimensions }), color: catColors.primary },
    nose: {
      ...generateNose({ height: catDimensions.height }),
      color: catColors.type === "dark" ? pink : dark,
    },
    colors: {
      background: generateBackgroundColors(),
      cat: catColors,
    },
    paths: {
      background: backgroundWaves,
      cat: generateBodyPath({ ...catDimensions }),
    },
  };
};
